import { Component, ViewChild, computed, input, inject, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PricePipe } from '../../../shared/pipes/price.pipe';
import { DatePipe, CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { PaymentHistory, PaymentHistoryItem } from '../../../models/payment-history.model';
import { PaymentHistoryService } from '../../../services/payment-history.service';
import { firstValueFrom } from 'rxjs';
import { BREAKPOINT_COMPACT } from '../../../shared/constants/breakpoints.constants';

/**
 * Component that displays loan payment history
 */
@Component({
  selector: 'app-payment-history',
  templateUrl: './payment-history.component.html',
  styleUrls: ['./payment-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    PricePipe,
    DatePipe
  ]
})
export class PaymentHistoryComponent {
@ViewChild('table', { static: false }) private table!: MatTable<PaymentHistoryItem[]>;

  readonly id = input.required<string>();
  readonly paymentHistory = signal<PaymentHistory | null>(null);
  isLoading = false;
  error: string | null = null;

  readonly #paymentHistoryService = inject(PaymentHistoryService);
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #expandedRows = signal<Set<number>>(new Set());

  readonly #isDesktop = toSignal(
    this.#breakpointObserver.observe(`(min-width: ${BREAKPOINT_COMPACT}px)`)  
  );
  readonly #columns = ['datePaid', 'type', 'amount'];

  // Use MatTableDataSource so the table picks up data changes
  readonly dataSource = new MatTableDataSource<PaymentHistoryItem[]>();
  readonly displayedColumns = computed(() => {
    const cols = this.#columns;
    return this.#isDesktop()?.matches ? cols : cols.filter(c => c !== 'expand');
  });

  // Whenever paymentHistory updates, update the dataSource.data
  readonly paymentGroups = computed(() => {
    const groups = this.paymentHistory()?.items ?? [];
    this.dataSource.data = groups;
    return groups;
  });

  constructor() {
    effect(async () => {
      if (!this.id()) {
        console.warn('Invalid loan ID:', this.id());
        return;
      }
      this.#loadLoanData(this.id());
    });
  }

// PaymentHistoryComponent
isDetailRow = (_: number, row: PaymentHistoryItem[]): boolean =>
  !!row?.[0]?.details?.length && this.isExpanded(row[0].transactionReferenceNo ?? 0);

  readonly #loadLoanData = async (loanId: string): Promise<void> => {
    this.paymentHistory.set(null);
    this.error = null;
    this.#expandedRows.set(new Set());

    if (isNaN(Number(loanId))) {
      this.error = 'Invalid loan ID';
      console.warn('Invalid loan ID:', loanId);
      return;
    }

    try {
      this.isLoading = true;
      const loadedHistory = await firstValueFrom(
        this.#paymentHistoryService.getPaymentHistory(loanId)
      );

      if (!loadedHistory) {
        throw new Error('Could not load loan data');
      }
      this.paymentHistory.set(loadedHistory);
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : 'Error loading loan data';
      console.error('Error loading loan data:', err);
    } finally {
      this.isLoading = false;
    }
  };

  hasDetails(item: PaymentHistoryItem): boolean {
    return (item.details ?? []).length > 0;
  }

  toggleRow(transactionReferenceNo: number): void {
    this.#expandedRows.update(set => {
      const next = new Set(set);
      if (next.has(transactionReferenceNo)) {
        next.delete(transactionReferenceNo);
      } else {
        next.add(transactionReferenceNo);
      }
      return next;
    });
    this.table.renderRows();
  }

  isExpanded(transactionReferenceNo: number): boolean {
    return this.#expandedRows().has(transactionReferenceNo);
  }

  /** calculate the total amount for a payment group */
  getGroupAmount(group: PaymentHistoryItem[]): number {
    const first = group[0];
    if (first.details?.length) {
      return first.details.reduce((sum, d) => sum + d.paymentAmount, 0);
    }
    return first.paymentAmount;
  }

  getDateFormat(): string {
    return this.#isDesktop()?.matches ? 'mediumDate' : 'shortDate';
  }

  onMobileRowClick(event: MouseEvent, transactionReferenceNo: number): void {
    if (!this.displayedColumns().includes('expand')) {
      this.toggleRow(transactionReferenceNo);
    }
  }
}
